import { FieldWrapper } from "@/components/ui/FieldWrapper";
import DatePicker from "@/components/ui/DatePicker";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import type { FormErrors, MandateForm, SetField } from "../types";

const CATEGORY_OPTIONS = [
  "Mobile Phone",
  "Laptop",
  "Two Wheeler",
  "Consumer Durable",
  "Furniture",
  "Medical Equipment",
  "Other",
].map((c) => ({ value: c, label: c }));

interface Props {
  form: MandateForm;
  errors: FormErrors;
  setField: SetField;
}

export default function ProductStep({ form, errors, setField }: Props) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Product Details</h3>
      <p className="text-[13px] text-ink-secondary">
        Details of the product being financed
      </p>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <Select
          label="Product Category"
          required
          placeholder="Select category"
          options={CATEGORY_OPTIONS}
          error={errors.category}
          id="category"
          value={form.category}
          onChange={(e) => setField("category", e.target.value)}
        />

        <Input
          label="Brand"
          required
          error={errors.brand}
          id="brand"
          placeholder="e.g. Samsung"
          value={form.brand}
          onChange={(e) => setField("brand", e.target.value)}
        />

        <Input
          label="Model"
          required
          error={errors.model}
          id="model"
          placeholder="e.g. Galaxy S24 Ultra"
          value={form.model}
          onChange={(e) => setField("model", e.target.value)}
        />

        <FieldWrapper
          label="Serial Number or IMEI"
          required
          error={errors.serialNo}
          htmlFor="serialNo"
          helper="IMEI for mobile phones, serial number for other products"
        >
          <input
            id="serialNo"
            placeholder="e.g. 351234567890123"
            value={form.serialNo}
            onChange={(e) =>
              setField("serialNo", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
            }
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.serialNo
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>

        <FieldWrapper label="Invoice Number" required error={errors.invoiceNo} htmlFor="invoiceNo">
          <input
            id="invoiceNo"
            placeholder="Enter invoice number"
            value={form.invoiceNo}
            onChange={(e) => setField("invoiceNo", e.target.value)}
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.invoiceNo
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>

        <FieldWrapper label="Invoice Date" required error={errors.invoiceDate} htmlFor="invoiceDate">
          <DatePicker
            id="invoiceDate"
            value={form.invoiceDate}
            placeholder="Select invoice date"
            onChange={(v) => setField("invoiceDate", v)}
          />
        </FieldWrapper>

        <div className="md:col-span-2">
          <Textarea
            label="Product Description"
            rows={2}
            maxLength={200}
            id="description"
            placeholder="Optional — additional notes about the product"
            helper={`${form.description.length}/200 characters`}
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}